/**
 * Thin API fetch wrapper.
 *
 * In dev: Vite proxy forwards `/api` → `localhost:8080`.
 * In prod: CloudFront forwards `/api` → ALB origin (see vite.config.ts comment).
 *
 * VITE_FUND_IMAGES_CF_DOMAIN is used to construct absolute CloudFront image
 * URLs from the relative S3 keys stored in DDB.
 */

const CF_DOMAIN = import.meta.env.VITE_FUND_IMAGES_CF_DOMAIN ?? "";

/**
 * Build a CloudFront URL for a fund image key stored in DDB.
 * Returns `undefined` when there's no key or the CloudFront domain isn't
 * configured (e.g. local dev without an env override) so callers can render a
 * graceful fallback instead of an empty `<img src="">` (which makes the browser
 * refetch the page URL).
 */
export function fundImageUrl(key: string | undefined): string | undefined {
  if (!key) return undefined;
  if (!CF_DOMAIN) return undefined;
  return `https://${CF_DOMAIN}/${key}`;
}

/**
 * Fetch a JSON resource from the API. Throws on non-2xx responses so TanStack
 * Query can surface them as errors without extra boilerplate per call-site.
 */
export async function apiFetch<T>(path: string): Promise<T> {
  const res = await fetch(path);
  if (!res.ok) {
    throw new Error(`API ${res.status} for ${path}`);
  }
  return res.json() as Promise<T>;
}
